import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Clock, ArrowUpRight } from "lucide-react";

// Curated "deep dive" guides per shop category (DB slug → blog slugs).
const GUIDES = {
  "retractables-banners": [
    "which-banner-material-vinyl-blockout-mesh-fabric-compared",
    "seg-silicone-edge-graphics-displays-faq",
    "13oz-vinyl-banner-faq",
    "18oz-blockout-banner-faq",
    "mesh-banner-70-30-faq",
    "9oz-fabric-banner-faq",
    "tension-fabric-banner-faq",
    "indoor-super-smooth-banner-faq",
    "retractable-banner-artwork-guide",
    "step-and-repeat-backdrop-guide",
    "10x10-custom-event-tent-guide",
    "same-day-banner-printing-houston",
  ],
  "custom-presentation-folders-pocket-folders": [
    "luxury-folders-hot-foil-white-ink-colorplan-ebony",
    "presentation-folders-houston",
    "gf-smith-colorplan-paper-guide",
    "letterpress-printing-one-color-pantone-colorplan",
  ],
  "roll-labels": [
    "recycled-kraft-roll-labels-eco-friendly",
    "custom-roll-labels-sizing-guide",
    "roll-label-material-guide",
    "die-cut-labels-shape-guide",
    "label-materials-ecommerce-brands",
    "white-bopp-synthetic-labels-guide",
    "clear-roll-labels-guide",
    "gold-roll-labels-guide",
    "silver-roll-labels-guide",
    "holographic-roll-labels-guide",
    "fluorescent-roll-labels-guide",
    "felt-roll-labels-guide",
    "ul-certified-roll-labels-guide",
    "cannabis-packaging-labels-state-compliance",
    "roll-labels-houston-guide",
  ],
  "digital-print": [
    "custom-business-cards-houston",
    "pantone-color-matching-coated-vs-uncoated-c-vs-u",
    "letterpress-printing-one-color-pantone-colorplan",
    "gf-smith-colorplan-paper-guide",
    "foil-stamped-invitations-houston",
    "shipping-foil-invitations-without-damage",
    "business-cards-nationwide-sales-team",
    "cmyk-vs-pantone-spot",
    "trade-show-printing-checklist-houston",
  ],
  "offset-print": [
    "cmyk-vs-pantone-spot",
    "pantone-color-matching-coated-vs-uncoated-c-vs-u",
    "custom-business-cards-houston",
    "foil-stamped-invitations-houston",
    "trade-show-print-first-time-exhibitors",
  ],
  "booklets-books": [
    "hardcover-book-spine-width-calculator",
    "hardcover-book-printing-guide",
    "hardcover-book-cover-design-guide",
    "hardcover-book-cost-and-timeline",
    "saddle-stitch-smyth-sewn-pur-perfect-binding",
    "booklet-printing-page-thresholds",
  ],
};

export const CategoryGuides = ({ categorySlug, categoryName }) => {
  const slugs = GUIDES[categorySlug] || [];
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    if (!slugs.length) { setPosts([]); return; }
    let live = true;
    import("../lib/blogPosts").then(({ getBlogPostBySlug }) => { if (live) setPosts(slugs.map(getBlogPostBySlug).filter(Boolean)); });
    return () => { live = false; };
  }, [categorySlug]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!posts.length) return null;
  return (
    <section className="mt-12" data-testid="category-guides">
      <div className="flex items-end justify-between gap-4 mb-5">
        <div>
          <div className="overline mb-1 flex items-center gap-2"><BookOpen className="w-3.5 h-3.5 text-[#B87333]" /> Deep dives</div>
          <h2 className="font-display font-black text-2xl md:text-3xl text-kolt-slate-dark uppercase tracking-tight">
            {categoryName} guides &amp; FAQs
          </h2>
        </div>
        <Link to="/blog" className="hidden sm:inline-flex items-center gap-1 font-mono text-[11px] tracking-[0.2em] uppercase font-bold text-kolt-slate-dark hover:text-kolt-orange" data-testid="category-guides-all">
          All articles <ArrowUpRight className="w-3.5 h-3.5" />
        </Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
        {posts.map((p) => (
          <Link
            key={p.slug}
            to={`/blog/${p.slug}`}
            className="group bg-white border border-slate-200 hover:border-[#B87333] transition-colors flex flex-col"
            data-testid={`category-guide-${p.slug}`}
          >
            <div className="aspect-[16/9] overflow-hidden bg-slate-100">
              <img src={p.image} alt={p.imageAlt || p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300" />
            </div>
            <div className="p-3 md:p-4 flex flex-col gap-2 flex-1">
              <div className="font-display font-black text-sm md:text-base text-kolt-slate-dark leading-tight line-clamp-3">{p.title}</div>
              <div className="mt-auto flex items-center gap-2 font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500">
                <Clock className="w-3 h-3" /> {p.readTime}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};
